import React, { useMemo, useState } from "react"

import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline"
import { PagyNav } from "~/src/components"
import { Bee } from "~/src/components/BeeKit"
import { SortMenu } from "~/src/components/Catalog/SortMenu"
import { iClient } from "~/src/lib/appClients"
import { Pagy, ProductCatalogItem } from "~/src/serializedRecords"
import { CatalogItemGrid } from "./CatalogItemGrid"

export type ProductCatalogContentProps = {
  meta: Pagy
  categories: string[]
  items: ProductCatalogItem[]
}

export function ProductCatalogContent(props: ProductCatalogContentProps) {
  const {
    items: initialItems,
    meta: { count, next, previous, series = [] },
  } = props

  const [items, setItems] = useState<ProductCatalogItem[]>(initialItems ?? [])
  const visibleItems = useMemo(() => items.filter((item) => !item.hidden), [items])

  const tabs = [
    { label: `All products (${count})`, ref: React.createRef() },
    { label: "Favorites", ref: React.createRef() },
    { label: `Hidden`, ref: React.createRef() },
    { label: "Past Orders", ref: React.createRef() },
  ]

  const handleLoveProduct = async (item: ProductCatalogItem) => {
    const currentLove = item.loved

    try {
      // Eagerly update "love" state in the UI
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, loved: !currentLove } : prevItem
        )
      )

      await iClient.put(`/product_preferences/${item.productId}/toggle/love`)
    } catch (error) {
      // Revert "love" state in the UI if server request failed
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, loved: currentLove } : prevItem
        )
      )

      throw error
    }
  }

  const handleHideProduct = async (item: ProductCatalogItem) => {
    const currentHidden = item.hidden

    try {
      // Eagerly update "hidden" state in the UI
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, hidden: !currentHidden } : prevItem
        )
      )

      await iClient.put(`/product_preferences/${item.productId}/toggle/hidden`)
    } catch (error) {
      // Revert "hidden" state in the UI if server request failed
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, hidden: currentHidden } : prevItem
        )
      )

      throw error
    }
  }

  return (
    <div className="flex flex-col mx-6 h-full">
      <Bee.Searchbar className="w-full mt-8" onSearch={(value: string) => alert(`Searching for: ${value}`)} />

      <Bee.TabBar className="mt-2">
        {tabs.map((tab, index) => (
          <Bee.Tab
            key={index}
            label={tab.label}
            isSelected={index === 0}
            ref={tab[index]}
            onClick={() => console.log("Tab clicked")}
          ></Bee.Tab>
        ))}
      </Bee.TabBar>

      <div className="flex items-center justify-between w-full mt-8">
        <div className="flex flex-row items-center hover:cursor-pointer py-2">
          <AdjustmentsHorizontalIcon className="w-4 h-4 mr-1" />
          <span className="text-sm">Filter</span>
        </div>
        <SortMenu />
      </div>

      <div className="h-full">
        <CatalogItemGrid onLoveProduct={handleLoveProduct} onHideProduct={handleHideProduct} items={visibleItems} />
      </div>

      <div className="flex justify-center p-2">
        <PagyNav previous={previous} next={next} series={series} />
      </div>
    </div>
  )
}
